// palbe.gen.ts emitter — TypeScript port of the CLI's Go endpoint emitter.
// The checked-in golden locks the complete app-bound runtime config and endpoint
// output shape.

import { type OAuthConfig, parseOAuthConfig } from '@palbase/auth';
import type { ErrorDef, Op, RoomDecl, Schema } from './parser.js';

export type { OAuthConfig } from '@palbase/auth';

// GeneratedConfig carries the runtime config values the generated palbe.gen.ts
// writes into __configure() (the web SDK seam — web embeds config in the
// generated module, unlike iOS which reads the per-env plist).
export interface GeneratedConfig {
  url: string;
  apiKey: string;
  appId: string;
  sealedRoot?: string;
  oauth?: OAuthConfig;
}

// --- Naming -----------------------------------------------------------------

export function opSegments(opId: string): string[] {
  return opId.split('.').filter((p) => p !== '');
}

function typeNameOf(s: string): string {
  return sanitize(s, true);
}

// typePrefix builds the PascalCase concatenation of all op-id segments, used
// as the BASE for top-level <Prefix>Request / <Prefix>Response / <Prefix>Error
// type names. Example: "rooms.create" → "RoomsCreate".
function typePrefix(opId: string): string {
  return opSegments(opId).map(typeNameOf).join('');
}

function sanitize(s: string, firstUpper: boolean): string {
  const parts: string[] = [];
  let cur = '';
  for (const ch of s) {
    if (/^[A-Za-z0-9]$/.test(ch)) {
      cur += ch;
    } else if (cur.length > 0) {
      parts.push(cur);
      cur = '';
    }
  }
  if (cur.length > 0) parts.push(cur);
  if (parts.length === 0) return firstUpper ? 'Op' : 'op';
  let out = '';
  parts.forEach((p, i) => {
    out +=
      i === 0 && !firstUpper
        ? p.charAt(0).toLowerCase() + p.slice(1)
        : p.charAt(0).toUpperCase() + p.slice(1);
  });
  if (out.charAt(0) >= '0' && out.charAt(0) <= '9') out = `_${out}`;
  return out;
}

// --- Emit -------------------------------------------------------------------

// emitTypeScript turns parsed operations into a palbe.gen.ts module.
// The file structure:
//   header → imports → __configure → types section → typed-errors section →
//   namespaces section (skip comments + __registerNamespaces) → declare module
export function emitTypeScript(
  ops: Op[],
  cfg: GeneratedConfig,
  rooms: RoomDecl[] = [],
  rolesArtifact = '',
): string {
  // Filter reserved top-level namespaces; collect skip comments.
  // Single-segment operationIds (the live wire shape — verb-prefixed ids like
  // `getHello`) pass the same filter and later register as ROOT-LEVEL
  // descriptor keys. Skip-comment dedup is case-insensitive (auth/Auth are the
  // same reserved surface); the first-seen casing is printed.
  const usable: Op[] = [];
  const skippedNS: string[] = [];
  const seenSkip = new Set<string>();
  for (const op of ops) {
    const [ns] = opSegments(op.operationId);
    if (ns === undefined) continue;
    if (tsReservedTopLevel(ns)) {
      const key = ns.toLowerCase();
      if (!seenSkip.has(key)) {
        seenSkip.add(key);
        skippedNS.push(ns);
      }
      continue;
    }
    usable.push(op);
  }
  skippedNS.sort();

  // Per-operation gates: nested-reserved / body+query / non-primitive-query /
  // kind-mismatch filtering happens BEFORE type emission, so skipped ops
  // neither emit dead types nor squat type-name prefixes against later ops.
  const [candidates, opSkips] = filterTSOps(usable);

  // Build all content sections FIRST (in-memory), so the import lines can be
  // derived from what is ACTUALLY emitted — an error-bearing op dropped by a
  // gate must not leave a stray `BackendError` import behind.
  const [typesOut, collisionSkips, typeCollisionOps] = emitTSTypes(candidates);

  // Type-collision ops have no usable types — they must not be callable-typed.
  const registerable = candidates.filter((op) => !typeCollisionOps.has(op.operationId));

  const [errorsOut, classCount] = emitTSTypedErrors(registerable);

  // --- Assemble ---
  let b = '';

  // Header.
  b += '// AUTO-GENERATED by `palbe-gen` — DO NOT EDIT.\n';
  b += '// Regenerate: palbe-gen   (or automatically via the predev/prebuild script)\n';

  // Imports. `BackendError` is referenced only by emitted error classes;
  // `CallOptions` only by augmentation method signatures. When neither is
  // used, the whole `import type ...` line is omitted — the runtime import
  // from '@palbase/web/internal' always stays.
  const typeImports: string[] = [];
  if (classCount > 0) typeImports.push('BackendError');
  if (registerable.length > 0) typeImports.push('CallOptions');
  if (typeImports.length > 0) {
    b += `import type { ${typeImports.join(', ')} } from '@palbase/web';\n`;
  }
  b += "import { __configure, __registerNamespaces } from '@palbase/web/internal';\n\n";

  b += emitTSConfigure(cfg);
  b += '\n';

  // Types + typed errors (either may be empty).
  b += typesOut;
  b += errorsOut;

  // Namespaces section.
  b += '// ── Namespaces ─────────────────────────────────────────────────────\n\n';
  // Skip comments: reserved-namespace, then per-op gates, then type collisions.
  for (const ns of skippedNS) {
    b += `// codegen: skipped reserved namespace "${commentSafe(ns)}"\n`;
  }
  for (const l of opSkips) {
    b += `${l}\n`;
  }
  for (const c of collisionSkips) {
    b += `${c}\n`;
  }
  b += emitTSRegistration(registerable);

  // declare module augmentation — omitted entirely when nothing registers.
  if (registerable.length > 0) {
    b += '\n';
    b += emitTSAugmentation(registerable);
  }

  // Rooms last, and only when there are any: a project that never declared one
  // must get the file it got before, byte for byte (NFR-004).
  if (rooms.length > 0) {
    b +=
      '\n// \u2500\u2500 Rooms (@Room) \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\n\n';
    for (const room of rooms) {
      b += emitTSRoom(room);
    }
  }

  // Roles last, and only when an artifact was handed over: a project whose last
  // `palbase spec` predates roles has none, and it must get the module it got
  // before, byte for byte. An artifact that is PRESENT but unreadable throws
  // from here rather than emitting nothing — see parseRolesArtifact.
  if (rolesArtifact !== '') {
    b += emitRoles(rolesArtifact);
  }

  return b;
}

// emitEnvironmentConfigModule renders the LEAF module the Next proxy imports.
//
// NOTHING IS IMPORTED HERE, and that is the entire point. Next compiles the
// proxy into its OWN bundle, so every module this file reaches is weight paid
// on every request path — and palbe.gen.ts reaches __configure -> buildRuntime
// -> messaging/wasm/loader (measured: 2.4 MB against 168 KB). A leaf has no
// graph to drag, so the proxy can read the selected environment without any of
// it. __tests__/next-proxy-bundle.test.ts measures that, it does not assume it.
//
// It carries the same two published values palbe.gen.ts is configured with —
// the api_key is always the publishable (anon) key, never service_role.
export function emitEnvironmentConfigModule(cfg: GeneratedConfig): string {
  let b = '// AUTO-GENERATED by `palbe-gen` — DO NOT EDIT.\n';
  b += '// The environment is chosen by PALBASE_ENV (default: local).\n';
  b += '//\n';
  b += '// IMPORT-FREE AND SIDE-EFFECT-FREE ON PURPOSE: the Next proxy reads the\n';
  b += '// environment from here precisely because this module reaches nothing else.\n';
  b += 'export const environmentConfig = {\n';
  b += `  url: ${tsStringLit(cfg.url)},\n`;
  b += `  apiKey: ${tsStringLit(cfg.apiKey)},\n`;
  if (cfg.sealedRoot) b += `  sealed: { root: ${tsStringLit(cfg.sealedRoot)} },\n`;
  b += '} as const;\n';
  return b;
}

// emitTSConfigure renders the __configure({…}) call, and re-exports
// environmentConfig from the sibling leaf module.
//
// WHY THE EXPORT: after this line the address and the publishable key live only
// inside the SDK's runtime, and the accessor for that runtime is `@internal` —
// stripped from the published types deliberately. Server code in a Next.js app
// legitimately needs the same plane (an OIDC leg a browser cannot walk, a
// webhook verifier, a server action) and would otherwise write the literals a
// second time.
//
// WHY IT IS A RE-EXPORT AND NOT A LITERAL: the Next proxy needs those same two
// values, and it CANNOT import this file — Next compiles the proxy into its own
// bundle and this module's __configure call reaches buildRuntime and from there
// livekit + the MLS WASM loader. So the values live in `palbe.config.ts`, an
// import-free leaf written beside this file, and both readers take them from
// there. __configure's own arguments stay literal: the leaf carries no appId
// and no oauth block, and configuring the runtime is this file's job.
function emitTSConfigure(cfg: GeneratedConfig): string {
  let b = '__configure({\n';
  b += `  url: ${tsStringLit(cfg.url)},\n`;
  b += `  apiKey: ${tsStringLit(cfg.apiKey)},\n`;
  b += `  appId: ${tsStringLit(cfg.appId)},\n`;
  if (cfg.sealedRoot) b += `  sealed: { root: ${tsStringLit(cfg.sealedRoot)} },\n`;
  const oauth = renderTSOAuth(cfg.oauth);
  if (oauth !== '') {
    b += '  oauth: {\n';
    b += oauth;
    b += '  },\n';
  }
  b += '});\n\n';
  b += '/** The Environment this file was generated against. Server code reads it from here. */\n';
  b += "export { environmentConfig } from './palbe.config';\n";
  return b;
}

// Emit the selected web snapshot without provider-specific field renaming.
function renderTSOAuth(o: OAuthConfig | undefined): string {
  if (o === undefined) return '';
  return JSON.stringify(parseOAuthConfig(o), null, 2)
    .split('\n')
    .slice(1, -1)
    .map((line) => `  ${line}\n`)
    .join('');
}

// --- Per-operation gates ------------------------------------------------

// filterTSOps applies the per-op gates that decide whether an operation can
// register at all: reserved nested segments, body+query conflict, query params
// the runtime's serializeQuery cannot handle, and namespace/method kind
// mismatches. Returns the surviving ops (input order preserved) plus loud
// skip-comment lines.
function filterTSOps(ops: Op[]): [Op[], string[]] {
  const out: Op[] = [];
  const skips: string[] = [];
  const root = new TSNode(); // shape-only trie for kind-mismatch detection

  for (const op of ops) {
    const segs = opSegments(op.operationId);
    const lastSeg = segs[segs.length - 1];
    if (lastSeg === undefined) continue;
    // Nested reserved check covers EVERY non-first segment — both the
    // intermediate namespace segments and the final method segment.
    let skip = false;
    for (const seg of segs.slice(1)) {
      if (tsReservedNested(seg)) {
        skips.push(
          `// codegen: skipped operation "${commentSafe(op.operationId)}" (reserved segment "${commentSafe(seg)}")`,
        );
        skip = true;
        break;
      }
    }
    if (skip) continue;
    // A descriptor's `input` is single-valued: body (default) or query.
    if (op.input && op.query) {
      skips.push(
        `// codegen: skipped operation "${commentSafe(op.operationId)}" (both body and query declared — unsupported)`,
      );
      continue;
    }
    // Non-primitive query params would make the runtime's serializeQuery throw.
    if (op.query) {
      const bad = firstNonPrimitiveQueryProp(op.query);
      if (bad !== undefined) {
        skips.push(
          `// codegen: skipped operation "${commentSafe(op.operationId)}" (non-primitive query parameter "${commentSafe(bad)}")`,
        );
        continue;
      }
    }
    // Kind-mismatch: a key claimed as both namespace and method.
    let node: TSNode | undefined = root;
    for (const seg of segs.slice(0, -1)) {
      node = node.childNode(seg);
      if (!node) break;
    }
    if (!node?.addMethod(lastSeg, op)) {
      skips.push(
        `// codegen: skipped operation "${commentSafe(op.operationId)}" (key collides with existing namespace/method)`,
      );
      continue;
    }
    out.push(op);
  }
  return [out, skips];
}

// firstNonPrimitiveQueryProp returns the name of the first query property whose
// schema cannot be serialized into a query string, or undefined when all are
// primitive.
function firstNonPrimitiveQueryProp(q: Schema): string | undefined {
  for (const p of q.props) {
    switch (p.schema.kind) {
      case 'string':
      case 'number':
      case 'integer':
      case 'boolean':
      case 'enum':
        break; // primitive — serializeQuery handles it
      default:
        return p.name;
    }
  }
  return undefined;
}

// --- Types section ----------------------------------------------------------

// emitTSTypes emits the `// ── Types ──` section: one named interface or type
// alias per op schema (request, response, query, error-data). Returns the
// rendered section (empty when no ops produce types), collision skip comment
// lines, and the set of colliding op ids.
function emitTSTypes(ops: Op[]): [string, string[], Set<string>] {
  const collisionOps = new Set<string>();
  // seenPrefix maps PascalCase typePrefix → first operationId that claimed it.
  const seenPrefix = new Map<string, string>();
  const collisionSkips: string[] = [];
  const lines: string[] = [];

  for (const op of ops) {
    const pfx = typePrefix(op.operationId);
    const first = seenPrefix.get(pfx);
    if (first !== undefined) {
      collisionSkips.push(
        `// codegen: skipped type "${commentSafe(pfx)}" (collides with operation "${commentSafe(first)}")`,
      );
      collisionOps.add(op.operationId);
      continue;
    }
    seenPrefix.set(pfx, op.operationId);

    // Request type (input body).
    if (op.input) {
      lines.push(...tsNamedTypeLines(`${pfx}Request`, op.input));
    }
    // Query type (emitted before Response, per golden contract order).
    if (op.query) {
      lines.push(...tsNamedTypeLines(`${pfx}Query`, op.query));
    }
    // Response type (output).
    if (op.output) {
      lines.push(...tsResponseTypeLines(pfx, op.output));
    }
    // Error data interfaces (after response) — only for errors that actually
    // lift; filtered errors emit no class, so a Data interface would orphan.
    const liftable = tsLiftableErrors(op);
    for (const e of liftable) {
      if (!e.data) continue;
      const dataName = `${pfx}${typeNameOf(e.name)}Data`;
      lines.push(`/** ${e.code} (${e.status}): ${e.description} */`);
      lines.push(...tsNamedTypeLines(dataName, e.data));
    }
  }

  if (lines.length === 0) {
    return ['', collisionSkips, collisionOps];
  }

  let b = '// ── Types ──────────────────────────────────────────────────────────\n\n';
  for (const l of lines) {
    b += `${l}\n`;
  }
  // Note: each type block ends with an empty-string entry that renders as a
  // blank separator line. No extra trailing newline needed here.
  return [b, collisionSkips, collisionOps];
}

// tsNamedTypeLines renders a named top-level type declaration: `export
// interface` for object schemas, `export type` alias otherwise.
function tsNamedTypeLines(name: string, s: Schema): string[] {
  if (s.kind === 'object' && !s.nullable && s.props.length > 0 && !s.additional) {
    return tsInterfaceLines(name, s);
  }
  return [`export type ${name} = ${tsTypeOf(s)};`, ''];
}

// tsResponseTypeLines handles the special case where a response is an array of
// objects: emits `<Prefix>ResponseItem` interface + `<Prefix>Response` alias.
function tsResponseTypeLines(pfx: string, s: Schema): string[] {
  if (
    s.kind === 'array' &&
    !s.empty &&
    s.elem &&
    s.elem.kind === 'object' &&
    !s.elem.nullable &&
    s.elem.props.length > 0 &&
    !s.elem.additional
  ) {
    return [
      ...tsInterfaceLines(`${pfx}ResponseItem`, s.elem),
      `export type ${pfx}Response = ${pfx}ResponseItem[]${s.nullable ? ' | null' : ''};`,
      '',
    ];
  }
  return tsNamedTypeLines(`${pfx}Response`, s);
}

// tsInterfaceLines renders `export interface Name { ... }` with sorted props.
// A property named __proto__ is skipped loudly: writing it in a JS object
// literal sets the prototype, and JSON.stringify drops it — wire-dead anyway.
function tsInterfaceLines(name: string, s: Schema): string[] {
  const lines: string[] = [`export interface ${name} {`];
  for (const p of s.props) {
    if (p.name === '__proto__') {
      lines.push('  // codegen: skipped property "__proto__"');
      continue;
    }
    const opt = p.required ? '' : '?';
    lines.push(`  ${tsPropKey(p.name)}${opt}: ${tsTypeOf(p.schema)};`);
  }
  lines.push('}', '');
  return lines;
}

// tsTypeOf converts a Schema to its TypeScript type string. For NESTED object
// schemas (appearing as property values) it emits an inline literal
// `{ k: T; ... }` rather than a named interface.
function tsTypeOf(s: Schema): string {
  switch (s.kind) {
    case 'string':
      return s.nullable ? 'string | null' : 'string';
    case 'number':
    case 'integer':
      return s.nullable ? 'number | null' : 'number';
    case 'boolean':
      return s.nullable ? 'boolean | null' : 'boolean';
    case 'null':
      return 'null';
    case 'literal': {
      const value =
        typeof s.literal === 'string' ? tsStringLit(s.literal) : JSON.stringify(s.literal);
      return s.nullable ? `${value} | null` : value!;
    }
    case 'union': {
      const members = (s.members ?? []).map(tsTypeOf);
      if (s.nullable) members.push('null');
      return members.join(' | ');
    }
    case 'enum': {
      const union = s.enumVals.map(tsStringLit).join(' | ');
      return s.nullable ? `(${union}) | null` : union;
    }
    case 'array': {
      if (s.empty) return s.nullable ? '[] | null' : '[]';
      if (!s.elem) {
        return s.nullable ? 'unknown[] | null' : 'unknown[]';
      }
      let elem = tsTypeOf(s.elem);
      // Parenthesise union element types (enum or nullable) so `T[]` parses.
      if (elem.includes(' | ') || elem.startsWith('(')) {
        elem = `(${elem})`;
      }
      const arr = `${elem}[]`;
      return s.nullable ? `${arr} | null` : arr;
    }
    case 'object': {
      const inline = tsInlineObject(s);
      return s.nullable ? `${inline} | null` : inline;
    }
    default: // 'any' or unknown
      return s.nullable ? 'unknown | null' : 'unknown';
  }
}

// tsInlineObject renders `{ k: T; k2?: U }` for a nested object schema.
// __proto__ props are dropped here too (no comment slot inside a single-line
// literal).
function tsInlineObject(s: Schema): string {
  const parts: string[] = [];
  for (const p of s.props) {
    if (p.name === '__proto__') continue;
    const opt = p.required ? '' : '?';
    parts.push(`${tsPropKey(p.name)}${opt}: ${tsTypeOf(p.schema)}`);
  }
  if (s.additional) {
    const record = `Record<string, ${tsTypeOf(s.additional)}>`;
    return parts.length === 0 ? record : `({ ${parts.join('; ')} } & ${record})`;
  }
  if (parts.length === 0)
    return s.additional === false ? 'Record<string, never>' : 'Record<string, unknown>';
  return `{ ${parts.join('; ')} }`;
}

// tsClientSynthesizedCodes are the error codes the web client produces ITSELF,
// for failures that never reached the backend (errors.ts, namespaces.ts,
// call.ts, upload.ts, auth-facade.ts, next/proxy.ts, and `fromEnvelope`'s
// fallback). A contract that declared one of them could not be told apart from
// the client's own error. `unauthorized` and `rate_limited` are NOT here: the
// client never makes them up, they are what the server answers — an endpoint
// throwing the SDK's `Unauthorized` declares `unauthorized`, and its typed class
// must reach the caller like any other.
const tsClientSynthesizedCodes = new Set([
  'not_configured',
  'network_error',
  'decode_error',
  'validation_error',
  'invalid_endpoint_name',
  'unsupported_get_input',
  'missing_path_param',
  'unexpected_argument',
  'invalid_query_value',
  'reserved_namespace',
  'invalid_namespace_tree',
  'aborted',
  'http_error',
]);

// tsLiftableErrors is the per-op set of typed error classes, sorted by wire
// code. Every error the contract declares gets one — an error left out would
// reach the caller as an untyped `BackendError` although the contract names it
// — so a declaration no class can honestly carry stops generation, naming the
// operation and the fix:
//   - a code the client synthesizes itself (tsClientSynthesizedCodes);
//   - `__proto__` (an object-literal key the lift map cannot hold);
//   - the same code twice;
//   - two errors whose class names collide (roomLocked + room_locked both
//     PascalCase to RoomLocked).
function tsLiftableErrors(op: Op): ErrorDef[] {
  if (op.errors.length === 0) return [];
  const refuse = (why: string): never => {
    throw new Error(`${op.operationId}: ${why} — rename the error in the backend and push again`);
  };
  const sorted = [...op.errors].sort((a, b) => (a.code < b.code ? -1 : a.code > b.code ? 1 : 0));
  const seenCode = new Set<string>();
  const seenClass = new Map<string, string>(); // PascalCase class-name stem → first error name
  for (const e of sorted) {
    if (e.code === '__proto__')
      refuse('declares the error code "__proto__", which no lookup can hold');
    if (tsClientSynthesizedCodes.has(e.code)) {
      refuse(
        `declares the error "${e.code}", a code the web client produces itself — the two could not be told apart`,
      );
    }
    if (seenCode.has(e.code)) refuse(`declares the error code "${e.code}" twice`);
    const stem = typeNameOf(e.name);
    const first = seenClass.get(stem);
    if (first !== undefined) {
      refuse(`declares errors "${first}" and "${e.name}", which both become the class ${stem}`);
    }
    seenCode.add(e.code);
    seenClass.set(stem, e.name);
  }
  return sorted;
}

// emitTSTypedErrors emits the `// ── Typed errors ──` section: one exported
// class per declared error across all registrable ops. Returns the section text
// plus the emitted class COUNT (the import line includes `BackendError` iff
// count ≥ 1).
function emitTSTypedErrors(ops: Op[]): [string, number] {
  let count = 0;
  const lines: string[] = [];
  for (const op of ops) {
    const pfx = typePrefix(op.operationId);
    const liftable = tsLiftableErrors(op);
    for (const e of liftable) {
      const className = `${pfx}${typeNameOf(e.name)}Error`;
      const hasData = e.data !== undefined;
      lines.push(`export class ${className} extends Error {`);
      lines.push(`  readonly name = '${className}';`);
      lines.push(`  readonly code = '${e.code}';`);
      lines.push(`  readonly status = ${e.status};`);
      if (hasData) {
        lines.push(`  readonly data: ${pfx}${typeNameOf(e.name)}Data;`);
      }
      lines.push('  readonly cause: BackendError;');
      lines.push('  constructor(cause: BackendError) {');
      lines.push('    super(cause.message);');
      lines.push('    this.cause = cause;');
      if (hasData) {
        lines.push(`    this.data = cause.data as ${pfx}${typeNameOf(e.name)}Data;`);
      }
      lines.push('  }');
      lines.push('}');
      lines.push('');
      count++;
    }
  }

  if (count === 0) return ['', 0];

  let b = '// ── Typed errors ───────────────────────────────────────────────────\n\n';
  for (const l of lines) {
    b += `${l}\n`;
  }
  return [b, count];
}

// --- Registration tree --------------------------------------------------

// TSNode is one level of the __registerNamespaces object literal. Entries keep
// insertion order (ops arrive sorted by operationId, so rendered keys come out
// alphabetically), each entry being either a nested namespace (child set) or a
// method descriptor (op set).
interface TSEntry {
  key: string;
  child?: TSNode;
  op?: Op;
}

class TSNode {
  entries: TSEntry[] = [];
  index = new Map<string, TSEntry>();

  // childNode returns (creating if needed) the nested namespace node for key.
  // Returns undefined if a METHOD entry already occupies the key (kind
  // mismatch).
  childNode(key: string): TSNode | undefined {
    const e = this.index.get(key);
    if (e) return e.child; // undefined when a method claims the key
    const child = new TSNode();
    const entry: TSEntry = { key, child };
    this.entries.push(entry);
    this.index.set(key, entry);
    return child;
  }

  // addMethod registers a method at key. Returns false if the key is already
  // occupied (either by another method or by a namespace node).
  addMethod(key: string, op: Op): boolean {
    if (this.index.has(key)) return false;
    const entry: TSEntry = { key, op };
    this.entries.push(entry);
    this.index.set(key, entry);
    return true;
  }
}

// buildTSTrie arranges PRE-FILTERED ops (filterTSOps survivors, possibly minus
// type-collision ops) into the namespace trie. Kind mismatches are impossible
// here — removing ops from a mismatch-free set never creates one.
function buildTSTrie(ops: Op[]): TSNode {
  const root = new TSNode();
  for (const op of ops) {
    const segs = opSegments(op.operationId);
    const lastSeg = segs[segs.length - 1];
    if (lastSeg === undefined) continue;
    let node: TSNode | undefined = root;
    for (const seg of segs.slice(0, -1)) {
      node = node.childNode(seg);
      if (!node) break; // unreachable: pre-filtered by filterTSOps
    }
    if (node) {
      node.addMethod(lastSeg, op);
    }
  }
  return root;
}

// emitTSRegistration renders the __registerNamespaces({…}) call (no section
// header or skip comments — those are written by emitTypeScript above). With
// zero ops the call renders single-line.
function emitTSRegistration(ops: Op[]): string {
  if (ops.length === 0) {
    return '__registerNamespaces({});\n';
  }
  let b = '__registerNamespaces({\n';
  b += renderTSNode(buildTSTrie(ops), 2);
  b += '});\n';
  return b;
}

// renderTSNode renders one tree level at the given indent, recursing into
// nested namespaces.
function renderTSNode(node: TSNode, indentSpaces: number): string {
  const ind = ' '.repeat(indentSpaces);
  let b = '';
  for (const e of node.entries) {
    if (e.child) {
      b += `${ind}${tsPropKey(e.key)}: {\n`;
      b += renderTSNode(e.child, indentSpaces + 2);
      b += `${ind}},\n`;
      continue;
    }
    if (e.op) {
      b += renderTSDescriptor(e.op, e.key, indentSpaces);
    }
  }
  return b;
}

// renderTSDescriptor renders one method descriptor entry at the given indent
// depth. Single-line when no errors survive filtering; multi-line when an
// errors map is emitted.
//
// Descriptor field rules:
//   - `method` and `path` always present
//   - `pathParams` present when op.pathParams is non-empty
//   - `input: 'none'` when no body and no query
//   - `input: 'query'` when query declared (body+query handled upstream)
//   - no `input` key when a body is declared (body is the default)
//   - `errors` map when liftable errors exist → forces multi-line
function renderTSDescriptor(op: Op, methodSeg: string, indentSpaces: number): string {
  const ind = ' '.repeat(indentSpaces);
  const key = tsPropKey(methodSeg);

  const liftable = tsLiftableErrors(op);
  const hasErrors = liftable.length > 0;

  const hasPathParams = op.pathParams.length > 0;
  const hasInput = op.input !== undefined;
  const hasQuery = op.query !== undefined;

  // Determine input field: '' = omit (body default).
  let inputVal = '';
  if (!hasInput && !hasQuery) {
    inputVal = 'none';
  } else if (hasQuery) {
    inputVal = 'query';
  }

  let b = '';
  if (!hasErrors) {
    // Single-line format: `key: { method: 'X', path: '...', [extras...] },`
    const parts = [`method: ${tsStringLit(op.method)}`, `path: ${tsStringLit(op.path)}`];
    if (hasPathParams) {
      parts.push(`pathParams: [${renderTSStringArray(op.pathParams)}]`);
    }
    if (inputVal !== '') {
      parts.push(`input: ${tsStringLit(inputVal)}`);
    }
    // The descriptor carries the marker so the runtime knows to consume the
    // body as a stream. Without it the signature would promise an iterable the
    // runtime never produces.
    if (op.sse) {
      parts.push('sse: true');
    }
    b += `${ind}${key}: { ${parts.join(', ')} },\n`;
    return b;
  }

  // Multi-line format.
  const innerInd = ' '.repeat(indentSpaces + 2);
  b += `${ind}${key}: {\n`;
  b += `${innerInd}method: ${tsStringLit(op.method)},\n`;
  b += `${innerInd}path: ${tsStringLit(op.path)},\n`;
  if (hasPathParams) {
    b += `${innerInd}pathParams: [${renderTSStringArray(op.pathParams)}],\n`;
  }
  if (inputVal !== '') {
    b += `${innerInd}input: ${tsStringLit(inputVal)},\n`;
  }
  if (op.sse) {
    b += `${innerInd}sse: true,\n`;
  }
  b += `${innerInd}errors: { `;
  liftable.forEach((e, i) => {
    if (i > 0) b += ', ';
    const className = `${typePrefix(op.operationId)}${typeNameOf(e.name)}Error`;
    b += `${tsPropKey(e.code)}: (e) => new ${className}(e)`;
  });
  b += ' },\n';
  b += `${ind}},\n`;
  return b;
}

// renderTSStringArray renders string-array literal elements: `'a', 'b'` (no
// surrounding brackets — caller adds them).
function renderTSStringArray(ss: string[]): string {
  return ss.map(tsStringLit).join(', ');
}

// --- Augmentation (declare module '@palbase/web') ---------------------------

// emitTSAugmentation renders the `declare module '@palbase/web' { interface PB
// { ... } }` block, mirroring the registration trie shape with typed method
// signatures. Ops arrive pre-filtered (same set as emitTSRegistration).
function emitTSAugmentation(ops: Op[]): string {
  let b = "declare module '@palbase/web' {\n";
  b += '  interface PB {\n';
  b += renderTSAugNode(buildTSTrie(ops), 4);
  b += '  }\n';
  b += '}\n';
  return b;
}

// renderTSAugNode renders augmentation entries at the given indent.
function renderTSAugNode(node: TSNode, indentSpaces: number): string {
  const ind = ' '.repeat(indentSpaces);
  let b = '';
  for (const e of node.entries) {
    if (e.child) {
      b += `${ind}${tsPropKey(e.key)}: {\n`;
      b += renderTSAugNode(e.child, indentSpaces + 2);
      b += `${ind}};\n`;
      continue;
    }
    if (e.op) {
      b += `${ind}${tsPropKey(e.key)}${renderTSMethodSignature(e.op)};\n`;
    }
  }
  return b;
}

// renderTSMethodSignature builds the TypeScript method signature for one op.
// Shape: (pathParam: string, ..., input?: T, options?: CallOptions): Promise<R>
function renderTSMethodSignature(op: Op): string {
  const pfx = typePrefix(op.operationId);
  const args: string[] = [];

  // Leading path params. Wire names are sanitized into valid TS identifiers
  // (`{user-id}` → userId, reserved words escaped, repeats deduped) — names
  // are purely positional: the runtime substitutes via the descriptor's
  // pathParams, which keep the WIRE names, so renaming here is safe.
  const used = new Set<string>();
  for (const p of op.pathParams) {
    args.push(`${tsParamIdent(p, used)}: string`);
  }

  // Input arg. A zero-prop object body still takes a real `input` arg: `{}` is
  // a valid wire body and the descriptor defaults to body.
  if (op.input) {
    args.push(`input: ${pfx}Request`);
  } else if (op.query) {
    args.push(`query: ${pfx}Query`);
  }

  // Options arg: fold headers when present.
  args.push(buildOptionsArg(op.headers));

  // A streaming op hands back the SEQUENCE. Typing it as `Promise<Response>`
  // would be a lie the compiler enforces on the caller: they would `await` once,
  // get one value, and never see the rest — the silent wrongness this extension
  // exists to prevent.
  const ret = op.sse
    ? `Promise<AsyncIterable<${pfx}Response>>`
    : op.output
      ? `Promise<${pfx}Response>`
      : 'Promise<void>';
  return `(${args.join(', ')}): ${ret}`;
}

// buildOptionsArg constructs the options parameter (with or without headers).
// Required when any declared header is required; optional otherwise.
function buildOptionsArg(headers: Schema | undefined): string {
  if (!headers || headers.props.length === 0) {
    return 'options?: CallOptions';
  }
  const anyRequired = headers.props.some((p) => p.required);

  const hParts = headers.props.map((p) => {
    const opt = p.required ? '' : '?';
    return `${tsPropKey(p.name)}${opt}: ${tsTypeOf(p.schema)}`;
  });
  const headersType = `{ ${hParts.join('; ')} }`;

  if (anyRequired) {
    return `options: CallOptions & { headers: ${headersType} }`;
  }
  return `options?: CallOptions & { headers?: ${headersType} }`;
}

// tsParamIdent converts a wire path-param name into a safe TS parameter
// identifier: camelCase-sanitized (`user-id` → userId), reserved words and the
// fixed signature arg names (input/query/options) escaped with a trailing
// underscore, repeats deduped with a numeric suffix. Records the chosen name.
function tsParamIdent(name: string, used: Set<string>): string {
  let id = sanitize(name, false);
  if (tsReservedWords.has(id) || id === 'input' || id === 'query' || id === 'options') {
    id += '_';
  }
  let candidate = id;
  for (let n = 2; used.has(candidate); n++) {
    candidate = id + n;
  }
  used.add(candidate);
  return candidate;
}

// tsReservedWords are ECMAScript keywords plus strict-mode reserved names that
// cannot be used as parameter binding identifiers in a module.
const tsReservedWords = new Set([
  'await',
  'break',
  'case',
  'catch',
  'class',
  'const',
  'continue',
  'debugger',
  'default',
  'delete',
  'do',
  'else',
  'enum',
  'export',
  'extends',
  'false',
  'finally',
  'for',
  'function',
  'if',
  'import',
  'in',
  'instanceof',
  'new',
  'null',
  'return',
  'super',
  'switch',
  'this',
  'throw',
  'true',
  'try',
  'typeof',
  'var',
  'void',
  'while',
  'with',
  'yield',
  // strict-mode reserved / restricted binding names
  'implements',
  'interface',
  'let',
  'package',
  'private',
  'protected',
  'public',
  'static',
  'arguments',
  'eval',
]);

// tsBareKeyRe matches identifiers that are valid bare TS object keys.
const tsBareKeyRe = /^[A-Za-z_$][A-Za-z0-9_$]*$/;

// tsPropKey renders an object-literal property key: bare when it is a valid
// identifier, single-quoted via tsStringLit otherwise (kebab-case segments,
// codes with dots, …).
function tsPropKey(name: string): string {
  return tsBareKeyRe.test(name) ? name : tsStringLit(name);
}

// commentSafe makes a server-supplied string safe to sit inside a `//` comment.
//
// Every `// codegen: skipped …` line below interpolates a name the GENERATOR
// did not choose — a role, a permission, an operationId, an error code — and a
// line comment ends at the first newline. A value carrying one closes the
// comment and everything after it becomes free TypeScript in the customer's
// generated module. Measured: the roles door accepts `a%0Ab`, chi decodes it,
// and the name reaches this file as `a\nb`.
//
// Nothing is dropped silently: the escapes stay visible so a reader sees WHY
// the name looks odd rather than seeing a truncated one. `*/` is neutralised
// too, so moving one of these lines into a block comment cannot reopen the
// hole.
function commentSafe(s: string): string {
  return s
    .replaceAll('\\', '\\\\')
    .replaceAll('\r', '\\r')
    .replaceAll('\n', '\\n')
    .replaceAll('*/', '*\\/');
}

// tsStringLit returns a single-quoted TypeScript string literal, escaping
// backslash and single quotes (and newlines/carriage returns defensively).
function tsStringLit(s: string): string {
  return `'${s
    .replaceAll('\\', '\\\\')
    .replaceAll("'", "\\'")
    .replaceAll('\n', '\\n')
    .replaceAll('\r', '\\r')}'`;
}

// tsReservedTopLevel reports whether the top-level namespace segment is
// reserved. Case-insensitive for the iOS SDK set (auth/analytics/flags/
// realtime) + case-sensitive for the web-only set (call, upload, then) and all
// Object.prototype members.
function tsReservedTopLevel(seg: string): boolean {
  switch (seg.toLowerCase()) {
    case 'auth':
    case 'analytics':
    case 'flags':
    case 'realtime':
      return true;
    default:
      return tsReservedWebOnly(seg);
  }
}

// tsReservedNested reports whether a non-first segment is reserved for nested
// positions: the Object.prototype members + `then`, but NOT `call` or `upload`
// (those are only reserved at the top level). Applied to every non-first
// segment including the final method segment.
function tsReservedNested(seg: string): boolean {
  if (seg === 'then') return true;
  return tsObjectPrototypeMember(seg);
}

// tsReservedWebOnly contains the web-only reserved set (call/upload/then) plus
// Object.prototype members. Case-sensitive.
function tsReservedWebOnly(seg: string): boolean {
  switch (seg) {
    case 'call':
    case 'upload':
    case 'then':
      return true;
    default:
      return tsObjectPrototypeMember(seg);
  }
}

// tsObjectPrototypeMember reports whether seg is an Object.prototype member.
function tsObjectPrototypeMember(seg: string): boolean {
  switch (seg) {
    case 'constructor':
    case 'hasOwnProperty':
    case 'isPrototypeOf':
    case 'propertyIsEnumerable':
    case 'toLocaleString':
    case 'toString':
    case 'valueOf':
    case '__proto__':
    case '__defineGetter__':
    case '__defineSetter__':
    case '__lookupGetter__':
    case '__lookupSetter__':
      return true;
    default:
      return false;
  }
}

/** The TS type name for a room: `ai:{sessionId}` \u2192 `AiSessionIdRoom`. */
function roomTypeName(pattern: string): string {
  return `${typeNameOf(pattern)}Room`;
}

/**
 * Emit one room as a descriptor an app hands to `pb.join`.
 *
 * The shape is chosen so an app writes NO strings and NO types of its own: the
 * pattern, the event names and the message names all live here. Events come out
 * as a discriminated union keyed by `event`, which is what makes a `switch` in
 * an app exhaustive \u2014 the compiler, not the developer, notices when the server
 * adds one.
 */
function emitTSRoom(room: RoomDecl): string {
  const name = roomTypeName(room.pattern);
  const lines: string[] = [];

  for (const e of room.events) {
    lines.push(...tsNamedTypeLines(`${name}${typeNameOf(e.name)}Event`, e.schema));
  }
  for (const m of room.messages) {
    lines.push(...tsNamedTypeLines(`${name}${typeNameOf(m.name)}Message`, m.schema));
  }

  // The union. `event` is the discriminant, so `switch (e.event)` narrows
  // `e.payload` with no cast anywhere in the app.
  if (room.events.length > 0) {
    lines.push(`export type ${name}Event =`);
    for (const e of room.events) {
      lines.push(`  | { event: '${e.name}'; payload: ${name}${typeNameOf(e.name)}Event }`);
    }
    lines[lines.length - 1] += ';';
  } else {
    lines.push(`export type ${name}Event = never;`);
  }
  lines.push('');

  // The descriptor. `send` exists only as a TYPE carrier \u2014 its value is never
  // read \u2014 so `room.send('say', {...})` checks the payload against the right
  // shape without the app ever naming it.
  const params = room.params.map((p) => `${tsPropKey(p)}: string`).join('; ');
  lines.push(`export interface ${name}Params { ${params} }`);
  lines.push(`export const ${name} = {`);
  lines.push(`  pattern: '${room.pattern}',`);
  lines.push(`  params: [${room.params.map((p) => `'${p}'`).join(', ')}] as const,`);
  // Runtime lists, not just types: `send` below is a TYPE carrier with no
  // runtime keys, so a JavaScript caller (which has no compiler) can only be
  // checked against these.
  lines.push(`  events: [${room.events.map((e) => `'${e.name}'`).join(', ')}] as const,`);
  lines.push(`  messages: [${room.messages.map((m) => `'${m.name}'`).join(', ')}] as const,`);
  lines.push(
    `  send: {} as { ${room.messages
      .map((m) => `${tsPropKey(m.name)}: ${name}${typeNameOf(m.name)}Message`)
      .join('; ')} },`,
  );
  lines.push(`  event: {} as ${name}Event,`);
  lines.push('} as const;');
  lines.push('');
  return lines.join('\n');
}

// --- Roles and permissions ---------------------------------------------------
//
// `palbase spec` writes the environment's role definitions beside its contract
// (inside the contract, `x-palbase-roles`); this turns them into two `as const` objects plus the
// union types derived from them, appended to the same generated module — so an
// app has ONE committed codegen artifact rather than a second one to wire up.
//
// Why generate them at all: a role and a permission are plain strings on the
// wire, and the failure mode of a typo is a 403 — the server is right, the code
// looks right, and nothing says which of the grants was meant. A constant turns
// that into a typecheck error. Withdrawal is the same story from the other side:
// a role deleted upstream disappears from the regenerated module, so code that
// still names it stops building instead of silently running without the access
// it thinks it has.
//
// `as const` plus `(typeof X)[keyof typeof X]` rather than a TypeScript `enum`,
// so the names autocomplete on BOTH sides — `Roles.moderator` as a value and
// `Role` as the type a function parameter can be annotated with — while the
// emitted values stay plain strings a JavaScript caller can pass too.
//
// There is deliberately no `userCount` and no assignment here. Who HAS a role is
// runtime state that changes without a deploy; what a role IS is the definition,
// and only the definition can be a type.

// RoleDecl is one role as the artifact carries it.
interface RoleDecl {
  name: string;
  // Absent when the role's name says everything — the artifact omits it rather
  // than writing '' (see the CLI's `stackRole`).
  description?: string;
  isDefault: boolean;
  permissions: string[];
}

// parseRolesArtifact reads the artifact strictly.
//
// A MISSING artifact is an environment that predates roles, and that is the
// caller's case to handle (no file, no section). A malformed one is a broken
// spec round, and quietly emitting nothing for it would hand the developer a
// module missing the very types they just added. The `roles` key specifically
// has to be present and an array: a proxy's JSON error page parses fine and
// would otherwise read as "this environment defines no roles".
function parseRolesArtifact(artifact: string): RoleDecl[] {
  let doc: unknown;
  try {
    doc = JSON.parse(artifact);
  } catch (e) {
    throw new Error(`roles artifact is not valid JSON: ${e instanceof Error ? e.message : e}`);
  }
  if (typeof doc !== 'object' || doc === null || !('roles' in doc) || !Array.isArray(doc.roles)) {
    throw new Error('roles artifact has no roles list');
  }
  return doc.roles.map((entry: unknown, i: number): RoleDecl => {
    if (typeof entry !== 'object' || entry === null) {
      throw new Error(`roles artifact entry ${i} is not a role`);
    }
    if (!('name' in entry) || typeof entry.name !== 'string' || entry.name === '') {
      throw new Error(`roles artifact entry ${i} has no name`);
    }
    const name = entry.name;
    if (!('isDefault' in entry) || typeof entry.isDefault !== 'boolean') {
      throw new Error(`role "${name}" does not say whether it is the default`);
    }
    if (!('permissions' in entry) || !Array.isArray(entry.permissions)) {
      throw new Error(`role "${name}" does not carry a list of permission names`);
    }
    const permissions: string[] = [];
    for (const permission of entry.permissions) {
      if (typeof permission !== 'string') {
        throw new Error(`role "${name}" lists a permission that is not a name`);
      }
      permissions.push(permission);
    }
    const role: RoleDecl = { name, isDefault: entry.isDefault, permissions };
    if (
      'description' in entry &&
      typeof entry.description === 'string' &&
      entry.description !== ''
    ) {
      role.description = entry.description;
    }
    return role;
  });
}

// emitRoles renders the roles section. Returns '' when the environment defines
// no roles, so a project that never adopted them gets a byte-identical module to
// the one it got before.
export function emitRoles(artifact: string): string {
  // Sorted here rather than trusted from the file: the artifact IS sorted today,
  // but this generator can also be handed one written by hand, and an output
  // whose order depends on its input's is an output nobody can diff.
  const roles = parseRolesArtifact(artifact).sort((a, b) =>
    a.name < b.name ? -1 : a.name > b.name ? 1 : 0,
  );

  // The permission object is the UNION across roles — a permission is a grant the
  // environment knows about, not a property of the one role that happens to list
  // it first. Two roles sharing `todos.read` must produce one key.
  const permissions = [...new Set(roles.flatMap((r) => r.permissions))].sort();

  const roleLines: string[] = [];
  const seenRoleKey = new Map<string, string>();
  for (const role of roles) {
    const key = sanitize(role.name, false);
    const first = seenRoleKey.get(key);
    if (first !== undefined) {
      roleLines.push(
        `  // codegen: skipped duplicate role "${commentSafe(role.name)}" — collides with "${commentSafe(first)}" (both map to \`${commentSafe(key)}\`)`,
      );
      continue;
    }
    seenRoleKey.set(key, role.name);
    // The description and the default flag are the only things the artifact
    // carries beyond the name, and a doc comment is where they belong: it reaches
    // the developer at the call site without becoming API surface the two
    // platforms would then have to keep identical.
    if (role.description !== undefined) {
      roleLines.push(`  /** ${singleLine(role.description)} */`);
    }
    if (role.isDefault) {
      roleLines.push('  /** Assigned to every new user at signup. */');
    }
    roleLines.push(`  ${key}: ${tsStringLit(role.name)},`);
  }

  const permissionLines: string[] = [];
  const seenPermissionKey = new Map<string, string>();
  for (const permission of permissions) {
    const key = sanitize(permission, false);
    const first = seenPermissionKey.get(key);
    if (first !== undefined) {
      permissionLines.push(
        `  // codegen: skipped duplicate permission "${commentSafe(permission)}" — collides with "${commentSafe(first)}" (both map to \`${commentSafe(key)}\`)`,
      );
      continue;
    }
    seenPermissionKey.set(key, permission);
    permissionLines.push(`  ${key}: ${tsStringLit(permission)},`);
  }

  // An empty `as const` object compiles, but the union derived from it resolves
  // to `never` — so every assignment fails with a message about a type nobody
  // wrote, while the module looks like it carries the feature. Each half appears
  // only when it has a key; with neither, the whole section does not exist.
  let b = '';
  if (seenRoleKey.size > 0) {
    b += 'export const Roles = {\n';
    b += `${roleLines.join('\n')}\n`;
    b += '} as const;\n\n';
    b += '/** One of the roles this environment defines. */\n';
    b += 'export type Role = (typeof Roles)[keyof typeof Roles];\n';
  }
  if (seenPermissionKey.size > 0) {
    if (b !== '') b += '\n';
    b += 'export const Permissions = {\n';
    b += `${permissionLines.join('\n')}\n`;
    b += '} as const;\n\n';
    b += "/** One of the permissions this environment's roles grant. */\n";
    b += 'export type Permission = (typeof Permissions)[keyof typeof Permissions];\n';
  }
  if (b === '') return '';

  let head = '\n// ── Roles and permissions ';
  head += '─'.repeat(42);
  head += '\n';
  head += '//\n';
  head += '// The roles this environment defines and the permissions they grant. A name\n';
  head += '// typed by hand is a string the server answers 403 to; a name typed here is a\n';
  head += '// compile error. Regenerated by `palbase spec` — a role withdrawn upstream\n';
  head += '// disappears from this file, so code still naming it stops building rather\n';
  head += '// than silently losing access.\n';
  head += '\n';
  return head + b;
}

// singleLine flattens a tenant-written description onto one line: it lands in a
// `/** … */` comment, and a `*/` sequence or a newline inside one ends the
// comment early and turns the rest of the sentence into code.
function singleLine(s: string): string {
  return s
    .split(/[\r\n]+/)
    .map((l) => l.trim())
    .filter((l) => l !== '')
    .join(' ')
    .replaceAll('*/', '*​/');
}
